import React, { createContext } from "react";
// UI Framework
// Hooks
import { useAppSelector } from "src/App/hooks";
// Common Components
import { CheckBox, FileDownloader, Button, Modal } from "src/Components";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import TableTemplate, {
  IExtraTypeUIProps,
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
// Local Components
import OrderBook from "./OrderBook";
import SearchParamUI from "./SearchParamUI";
import SettingModal from "./SettingModal";
import ChartSearchParamUI from "../ChartSearchParamUI";
import FilterSettingModal from "./FilterSettingsModal";
// Requests
import {
  getEmergencyRollTable,
  getEmergencyRollExcel,
  getUpRollTable,
  getDownRollTable,
  getRollDownExcel,
  getRollUpExcel,
} from "src/Requests/coveredCall";
// Utilities
import * as R from "ramda";
// Types
import { ExtraType } from "src/Interfaces/TableInterfaces";
import {
  IRollSearchParam,
  ISelectedTableInfo,
  RollReport,
} from "src/Interfaces/coveredCall";
import { Additional } from "./Additional";

interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}

enum ModalMode {
  Closed,
  RollSettings,
  FilterSetting,
}

interface IProps {
  position: "up" | "down" | "emergency";
}

export const CoveredCallRollContext = createContext({ riskProfileId: "" });

const RollCalculator: React.FC<IProps> = ({ position }) => {
  // refs
  const prevRowRef = React.useRef<number | null>(null);
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [selectedTableInfo, setSelectedTableInfo] =
    React.useState<ISelectedTableInfo>({} as ISelectedTableInfo);
  const [searchParam, setSearchParam] = React.useState<IRollSearchParam>({
    riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
  } as IRollSearchParam);
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IRollSearchParam>({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
    } as IRollSearchParam);
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [visibleHeaders, setVisibleHeaders] = React.useState(
    {} as IVisibleHeaders,
  );
  const [refreshFlag, setRefreshFlag] = React.useState(false);

  React.useEffect(() => {
    setSearchParam({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
    } as IRollSearchParam);
    setServerSearchParams({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
    } as IRollSearchParam);
    setSelectedTableInfo({} as ISelectedTableInfo);
  }, [position]);

  const changeHandler = (name: string, value: any) => {
    setVisibleHeaders({ ...visibleHeaders, [name]: value } as IVisibleHeaders);
  };

  const handleExcelDownload = () => {
    switch (position) {
      case "emergency":
        return getEmergencyRollExcel(searchParam);
      case "down":
        return getRollDownExcel(searchParam);
      case "up":
        return getRollUpExcel(searchParam);
    }
  };

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setServerSearchParams(cleanObject as IRollSearchParam);
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "coveredCallPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const getDataTable = () => {
    switch (position) {
      case "emergency":
        return getEmergencyRollTable;
      case "up":
        return getUpRollTable;
      case "down":
        return getDownRollTable;
    }
  };

  const riskProfileChangeHandler = (name: string, value: any) => {
    searchParamChangeHandler(name, value);
    setServerSearchParams({ ...serverSearchParams, [name]: value });
  };

  const UIModal = React.useMemo(() => {
    switch (modalMode) {
      case ModalMode.FilterSetting:
        return (
          <FilterSettingModal
            position={position}
            onClose={() => setModalMode(ModalMode.Closed)}
            onApplied={() => setRefreshFlag((prev) => !prev)}
          />
        );
      case ModalMode.RollSettings:
        return (
          <SettingModal
            position={position}
            onClose={() => setModalMode(ModalMode.Closed)}
          />
        );
    }
    return null;
  }, [modalMode]);

  const getExtraTypeUI = ({
    headerName,
    extraType,
    row,
    cellType,
    translateHeaderName,
  }: IExtraTypeUIProps<RollReport>) => {
    switch (extraType) {
      case ExtraType.WithChart:
        return <ChartSearchParamUI {...{ row, headerName, translateHeaderName }} />;

      default:
        return <></>;
    }
  };

  return (
    <div className="flex flex-col justify-start items-start gap-2 relative min-h-screen pt-5 mx-auto p-8 w-full h-full">
      {modalMode ? (
        <Modal
          UI={UIModal}
          onClose={() => setModalMode(ModalMode.Closed)}
          modalBoxClassName={
            modalMode === ModalMode.RollSettings ? "!w-[90vw]" : ""
          }
        />
      ) : (
        <></>
      )}
      <div className="flex justify-start items-center w-full">
        <div className="flex flex-row justify-start items-center gap-5">
          <div className="bg-sky-200 rounded py-1 px-2">
            <CheckBox
              checked={visibleHeaders.all}
              label="جزئیات"
              name="all"
              onChange={changeHandler}
            />
          </div>
          <div className="bg-red-200 rounded py-1 px-2">
            <CheckBox
              checked={visibleHeaders.mediator}
              label="محاسبات"
              name="mediator"
              onChange={changeHandler}
            />
          </div>
          <FileDownloader getFile={handleExcelDownload} />
          <Button
            onClick={() => setModalMode(ModalMode.RollSettings)}
            text="تنظیمات رول"
            variant="outlined"
          />

          <Button
            onClick={() => setModalMode(ModalMode.FilterSetting)}
            text="تنظیمات فیلتر"
            variant="outlined"
          />
        </div>
      </div>
      <OrderBook selectedTableInfo={selectedTableInfo} />
      <div className="flex flex-row justify-start items-start gap-1 w-full mt-7 h-fit">
        <SearchParamUI
          searchParam={searchParam}
          searchParamChangeHandler={searchParamChangeHandler}
          onSubmit={handleSearch}
        />
        <div className="border border-gray-300 rounded flex min-w-0 flex-1 flex-col p-4 justify-center items-center gap-3 overflow-x-hidden">
          <SelectableButtons
            label="ریسک پروفایل"
            name="riskProfileId"
            onChange={riskProfileChangeHandler}
            options={riskProfiles}
            value={serverSearchParams?.riskProfileId}
            hideSelectAll
          />
          <CoveredCallRollContext.Provider
            value={{ riskProfileId: serverSearchParams?.riskProfileId }}
          >
            <TableTemplate<IRollSearchParam, RollReport>
              TableUI={({ reports, onAction, sortingType, setSortingType }) => (
                <CommonTableUI
                  reports={reports}
                  onAction={onAction}
                  AdditionalBody={Additional}
                  filterCols={getFilterCols()}
                  sortingType={sortingType}
                  setSortingType={setSortingType}
                  refreshFlag={refreshFlag}
                  onTableRowClick={(row) => {
                    if (prevRowRef.current === row.id) return;
                    prevRowRef.current = row.id;
                    setSelectedTableInfo({
                      id: row.id,
                      insIds: row.insIds,
                    });
                  }}
                  activeRowId={
                    selectedTableInfo ? selectedTableInfo?.id : undefined
                  }
                  tableBoxClassName="max-h-[500px]"
                  ref={prevRowRef}
                  getExtraTypeUI={getExtraTypeUI}
                />
              )}
              refreshTableTime={30000}
              getDataTable={getDataTable()}
              searchParameters={serverSearchParams}
              withSort
            />
          </CoveredCallRollContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default RollCalculator;
